const GA_MEASUREMENT_ID = "G-QLV0T58MD2"
const CONSENT_KEY = "chanoyu-analytics-consent"

export type AnalyticsConsent = "granted" | "denied"

const getWindow = () =>
  typeof window !== "undefined" ? (window as any) : null

const isProductionHost = () => {
  const win = getWindow()

  if (!win) {
    return false
  }

  return (
    win.location.hostname === "chanoyu.fr" ||
    win.location.hostname === "www.chanoyu.fr"
  )
}

export const getAnalyticsConsent = (): AnalyticsConsent | null => {
  const win = getWindow()

  if (!win) {
    return null
  }

  try {
    const value = win.localStorage.getItem(CONSENT_KEY)
    return value === "granted" || value === "denied" ? value : null
  } catch {
    return null
  }
}

const setAnalyticsConsent = (value: AnalyticsConsent) => {
  const win = getWindow()

  if (!win) {
    return
  }

  try {
    win.localStorage.setItem(CONSENT_KEY, value)
  } catch {
    // Le choix reste valable pour la session si le stockage est indisponible.
  }
}

const initializeGtag = () => {
  const win = getWindow()

  if (!win) {
    return
  }

  win.dataLayer = win.dataLayer || []

  if (!win.gtag) {
    win.gtag = function () {
      win.dataLayer.push(arguments)
    }
  }
}

export const loadGoogleAnalytics = (): boolean => {
  const win = getWindow()

  if (
    !win ||
    getAnalyticsConsent() !== "granted" ||
    !isProductionHost()
  ) {
    return false
  }

  initializeGtag()

  if (!document.querySelector('script[data-chanoyu-ga4="true"]')) {
    const script = document.createElement("script")
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    script.dataset.chanoyuGa4 = "true"
    document.head.appendChild(script)
  }

  if (!win.__chanoyuGa4Configured) {
    win.gtag("js", new Date())
    win.gtag("config", GA_MEASUREMENT_ID)
    win.__chanoyuGa4Configured = true
  }

  return true
}

export const acceptAnalytics = () => {
  setAnalyticsConsent("granted")
  loadGoogleAnalytics()
}

const deleteAnalyticsCookies = () => {
  const win = getWindow()

  if (!win || typeof document === "undefined") {
    return
  }

  const hostname = win.location.hostname
  const cookieNames = document.cookie
    .split(";")
    .map(cookie => cookie.split("=")[0].trim())
    .filter(name => name.startsWith("_ga"))

  cookieNames.forEach(name => {
    document.cookie = `${name}=; Max-Age=0; path=/`
    document.cookie = `${name}=; Max-Age=0; path=/; domain=${hostname}`
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.${hostname}`
  })
}

export const denyAnalytics = () => {
  const wasGranted = getAnalyticsConsent() === "granted"

  setAnalyticsConsent("denied")
  deleteAnalyticsCookies()

  // Si GA4 avait déjà été chargé, un rechargement retire le script
  // et repart avec le consentement refusé.
  if (wasGranted && isProductionHost()) {
    window.location.reload()
  }
}

export const openAnalyticsPreferences = () => {
  const win = getWindow()

  if (!win) {
    return
  }

  win.dispatchEvent(new Event("chanoyu:open-analytics-preferences"))
}
